import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Firestore, collection, doc, getDocs, setDoc, deleteDoc, orderBy, query } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { ImageUploadService } from '../../services/image-upload.service';

interface ContentBlock {
  type: 'text' | 'title' | 'image';
  value: string;
}

interface Article {
  id?: string;
  slug: string;
  title: string;
  content: string;
  blocks: ContentBlock[];
  order: number;
}

interface Category {
  id: string;
  title: string;
  description: string;
  articles: Article[];
  expanded: boolean;
  order: number;
}

@Component({
  selector: 'app-knowledge-base',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './knowledge-base.component.html'
})
export class KnowledgeBaseComponent implements OnInit {
  searchTerm = '';
  selectedArticle: { category: Category; article: Article } | null = null;
  categories: Category[] = [];
  loading = true;

  // Edit state
  editingArticle = false;
  editTitle = '';
  editBlocks: ContentBlock[] = [];
  uploadingBlockIndex: number | null = null;

  // Add category
  showAddCategory = false;
  newCategoryTitle = '';
  newCategoryDescription = '';

  // Add article
  addingArticleToCategoryId: string | null = null;
  newArticleTitle = '';

  isAdmin = false;

  constructor(
    private firestore: Firestore,
    private auth: Auth,
    private imageUploadService: ImageUploadService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  async ngOnInit() {
    this.isAdmin = this.auth.currentUser?.email === 'mvangastel@gmail.com';
    await this.loadFromFirestore();

    // Open article from URL params (slug-based)
    const catSlug = this.route.snapshot.paramMap.get('categoryId');
    const artSlug = this.route.snapshot.paramMap.get('articleId');
    if (catSlug && artSlug) {
      const cat = this.categories.find(c => c.id === catSlug);
      const art = cat?.articles.find(a => a.slug === artSlug);
      if (cat && art) {
        cat.expanded = true;
        this.selectedArticle = { category: cat, article: art };
      }
    }
  }

  private async loadFromFirestore() {
    this.loading = true;
    try {
      const catsRef = collection(this.firestore, 'knowledge_base');
      const catsSnap = await getDocs(query(catsRef, orderBy('order', 'asc')));

      const categories: Category[] = [];
      for (const catDoc of catsSnap.docs) {
        const data = catDoc.data();
        const articlesRef = collection(this.firestore, 'knowledge_base', catDoc.id, 'articles');
        const articlesSnap = await getDocs(query(articlesRef, orderBy('order', 'asc')));
        const articles: Article[] = articlesSnap.docs.map(aDoc => {
          const d = aDoc.data();
          const blocks: ContentBlock[] = d['blocks'] || [];
          if (blocks.length === 0 && d['content']) {
            blocks.push({ type: 'text', value: d['content'] });
          }
          const title = d['title'] || '';
          return {
            id: aDoc.id,
            slug: d['slug'] || this.toSlug(title),
            title,
            content: d['content'] || '',
            blocks,
            order: d['order'] || 0
          };
        });

        categories.push({
          id: catDoc.id,
          title: data['title'] || '',
          description: data['description'] || '',
          articles,
          expanded: false,
          order: data['order'] || 0
        });
      }

      this.categories = categories;

      // Seed default content if empty
      if (this.categories.length === 0) {
        await this.seedDefaultContent();
      }
    } catch (e) {
      console.error('Error loading knowledge base:', e);
    } finally {
      this.loading = false;
    }
  }

  private async seedDefaultContent() {
    const defaults = [
      {
        id: 'getting-started', title: 'Aan de slag', description: 'De basis om te beginnen', order: 0,
        articles: [
          { title: 'Inloggen', content: 'Na het ontvangen van je inloggegevens kun je inloggen via het inlogscherm. Je ontvangt een e-mailadres en wachtwoord van de beheerder.\n\nNa het inloggen kom je op het dashboard terecht waar je direct kunt beginnen met het beheren van je website.', order: 0 },
          { title: 'Domein selecteren', content: 'Als je meerdere websites beheert, kun je rechtsboven in de navigatie het actieve domein wisselen. Klik op de domeinnaam en selecteer het gewenste domein.\n\nAlle wijzigingen die je maakt zijn altijd gekoppeld aan het geselecteerde domein.', order: 1 },
          { title: 'Taal instellen', content: 'Het CMS ondersteunt meerdere talen. In de instellingen kun je de beschikbare talen configureren. Per pagina of post kun je content in verschillende talen aanmaken.\n\nDe standaardtaal wordt automatisch getoond als een vertaling niet beschikbaar is.', order: 2 }
        ]
      },
      {
        id: 'pages', title: "Pagina's beheren", description: "Pagina's aanmaken en bewerken", order: 1,
        articles: [
          { title: "Pagina's overzicht", content: "In het pagina-overzicht zie je alle pagina's van je website. Je kunt hier pagina's aanmaken, bewerken en verwijderen.\n\nElke pagina heeft een route (URL-pad), een titel en kan meerdere componenten bevatten die samen de pagina vormen.", order: 0 },
          { title: 'De Page Editor', content: "De Page Editor is het hart van het CMS. Hier bouw je pagina's op met componenten.\n\nDe linker sidebar toont een boomstructuur van alle componenten (Layers tab) en beschikbare componenten (Componenten tab).\n\nHet midden toont een live preview. Klik op een component om het te selecteren.\n\nDe rechter sidebar toont het bewerkingspaneel met alle opties voor het geselecteerde component.", order: 1 },
          { title: 'Titel Sectie', content: 'De Titel Sectie toont een grote koptekst met optionele subtitel.\n\nInstelbaar: titel, subtitel (boven of onder), HTML tag (H1-H6), grootte, dikte, uitlijning, scheidingslijn en achtergrond (kleur, afbeelding of thema-accent).', order: 2 },
          { title: 'Tekst Component', content: 'Het Tekst Component bevat een WYSIWYG editor waarmee je opgemaakte tekst kunt schrijven.\n\nJe kunt tekst opmaken met vet, cursief, koppen, lijsten en meer. Afbeeldingen invoegen kan ook direct in de editor. Deze worden automatisch gecomprimeerd en als WebP opgeslagen.', order: 3 },
          { title: 'Media Galerij', content: "De Media Galerij toont afbeeldingen in verschillende layouts:\n\nGrid: raster met instelbaar aantal kolommen\nCarousel: horizontaal scrollende slider met navigatiepijlen\nHero: enkele grote afbeelding op volledige breedte\nLogo Ticker: doorlopend scrollende logo's\nMasonry: Pinterest-achtige layout\n\nPer afbeelding kun je een subtitel, titel, beschrijving en CTA buttons instellen.", order: 4 },
          { title: 'Grid Layout', content: 'Het Grid Layout component maakt een raster met kolommen waarin je andere componenten kunt plaatsen.\n\nJe kunt het aantal kolommen en rijen instellen, de gap (ruimte tussen cellen), en achtergrondkleur per cel.\n\nKlik op een cel om er componenten aan toe te voegen. Je kunt meerdere componenten per cel hebben en de volgorde wijzigen met pijltjes.', order: 5 },
          { title: 'Formulier Component', content: 'Met het Formulier Component maak je contactformulieren.\n\nVelden toevoegen: klik op "Veld toevoegen". Per veld kun je instellen: label, type (tekst, e-mail, telefoon, tekstvak, dropdown, checkbox), verplicht, breedte (vol of half) en placeholder.\n\nVul een notificatie e-mailadres in om bij elke inzending een notificatie te ontvangen.', order: 6 },
          { title: 'Kaart Component', content: 'Het Kaart Component toont een interactieve kaart.\n\nInstelbaar: locatie (latitude/longitude), zoomniveau, hoogte, markers met titel en beschrijving, zoom-bediening en scroll-zoom.', order: 7 },
          { title: 'Action Element', content: 'Het Action Element voegt knoppen toe.\n\nVarianten: Button (enkele knop), Banner (gekleurde achtergrond met titel en knoppen), CTA (call-to-action sectie).\n\nButton stijlen: primary (gevuld), secondary (alternatief), outline (transparant met rand). Je kunt een tweede knop toevoegen naast de primaire.', order: 8 },
          { title: 'Padding en Margin', content: 'Elk component heeft padding en margin instellingen onder het Spacing gedeelte.\n\nPadding: de ruimte binnen het component (boven, onder, links/rechts).\nMargin: de ruimte buiten het component (boven, onder).\n\nKlik op Padding of Margin om de schuifregelaars te tonen.', order: 9 }
        ]
      },
      {
        id: 'menu', title: 'Menu beheren', description: 'De navigatie van je website instellen', order: 2,
        articles: [
          { title: 'Menu-items toevoegen', content: 'Je kunt vier soorten items aan het menu toevoegen:\n\nPagina: selecteer een bestaande pagina\nURL: voeg een externe of interne link toe\nPost: koppel een blogpost als menu-item\nProject: voeg een project toe aan het menu\n\nKlik op de bijbehorende knop bovenaan de menu-editor.', order: 0 },
          { title: 'Menu structuur', content: 'Sleep menu-items om ze te herordenen. Je kunt items nesten door ze onder een ander item te slepen. Geneste items verschijnen als dropdown/submenu op de website.', order: 1 },
          { title: 'Menu weergave', content: 'Per menu-item kun je kiezen uit drie weergavetypen:\n\nStandaard: normaal dropdownmenu\nMega Menu: breed uitklapmenu met meerdere kolommen\nReclame: menu-item met highlight-afbeelding, titel, beschrijving en CTA', order: 2 }
        ]
      },
      {
        id: 'posts', title: 'Posts / Blog', description: 'Blogposts en nieuwsberichten beheren', order: 3,
        articles: [
          { title: 'Posts aanmaken', content: 'Ga naar Posts en klik op "Nieuw bericht".\n\nVerplichte velden: titel, URL-slug (automatisch gegenereerd), minimaal een domein.\n\nOptionele velden: content (WYSIWYG editor), uitgelichte afbeelding, categorieen, meta-titel, meta-beschrijving, meta-keywords en OG-afbeelding.', order: 0 },
          { title: 'Categorieen', content: 'Posts worden georganiseerd in categorieen. Ga naar Posts > Categorieen.\n\nPer categorie kun je instellen: naam, slug (URL-pad), vertalingen en subcategorieen.\n\nDe categorie-slug bepaalt de URL-prefix van posts. Bijvoorbeeld: categorie "Producten" met slug "producten" maakt URLs als /producten/mijn-post.', order: 1 },
          { title: 'Meertalige posts', content: 'Posts ondersteunen meerdere talen. Bovenaan de post-editor kun je de taal wisselen.\n\nElke taalversie is een apart document maar ze zijn gekoppeld. Zo kun je per taal een andere titel, content en slug hebben.', order: 2 },
          { title: 'Posts importeren', content: 'Via Posts > Importeren kun je posts importeren vanuit een WordPress website.\n\nVoer de WordPress JSON API URL in en de beschikbare posts worden opgehaald. Per post kun je kiezen of je deze wilt importeren.', order: 3 }
        ]
      },
      {
        id: 'media', title: 'Media', description: 'Afbeeldingen en bestanden beheren', order: 4,
        articles: [
          { title: 'Media uploaden', content: 'Bij elke upload worden automatisch geoptimaliseerde varianten aangemaakt:\n\nOrigineel: het ongewijzigde bestand\nWebP 1400px: grote variant voor volledige breedte\nWebP 600px: medium variant voor kaarten en previews\nWebP 200px: kleine variant voor thumbnails\n\nDe WebP-varianten zijn aanzienlijk kleiner, wat zorgt voor snellere laadtijden.', order: 0 },
          { title: 'Media selector', content: 'In de page editor kun je afbeeldingen selecteren via de Media Selector popup. Je kunt ook direct een nieuwe afbeelding uploaden vanuit de popup.', order: 1 }
        ]
      },
      {
        id: 'settings', title: 'Instellingen', description: 'Website-instellingen, thema en SEO', order: 5,
        articles: [
          { title: 'SEO instellingen', content: 'Onder Instellingen kun je SEO configureren:\n\nHeader tags: code in de head van elke pagina (bijv. Google Analytics)\nBody tags: code na de opening body tag\nFooter tags: code voor de sluitende body tag\nGoogle Analytics ID: voor bezoekerstatistieken', order: 0 },
          { title: 'Thema en kleuren', content: 'Pas het uiterlijk van je website aan.\n\nKleuren: primaire kleur, achtergrondkleur, tekstkleur, linkkleur.\n\nTypografie: lettertype (Google Fonts), lettergrootte, regelhoogte, koppen stijl (H1-H6).\n\nKnoppen: primaire en secundaire knop kleuren, hoekafronding.', order: 1 },
          { title: 'Navigatie instellingen', content: 'De navigatie kan uitgebreid worden geconfigureerd:\n\nMenu positie: links, midden of rechts\nLogo positie: links, midden, rechts of apart\nAchtergrondkleur en transparantie\nHoogte van de navigatiebalk\nCTA knop: optionele call-to-action knop\nTopbar: balk boven de navigatie met contactgegevens en social media', order: 2 },
          { title: 'Talen configureren', content: 'Je kunt meerdere talen toevoegen:\n\n1. Ga naar Instellingen\n2. Klik op "Taal toevoegen"\n3. Selecteer een land/taal uit de lijst\n\nBezoekers kunnen de taal wisselen via de taalselector in de navigatie.', order: 3 }
        ]
      },
      {
        id: 'users', title: 'Gebruikers', description: 'Gebruikers en rollen beheren', order: 6,
        articles: [
          { title: 'Rollen en rechten', content: 'Het CMS heeft vier rollen:\n\nSuperadmin: volledige toegang tot alle functies, kan gebruikers beheren en domeinen toevoegen/verwijderen.\n\nAdmin: toegang tot webshop, posts, import, gebruikersbeheer.\n\nEditor: kan posts aanmaken en bewerken, categorieen beheren. Geen toegang tot webshop of gebruikersbeheer.\n\nSubscriber: alleen leestoegang.', order: 0 }
        ]
      },
      {
        id: 'tips', title: 'Tips', description: 'Handige tips voor efficient werken', order: 7,
        articles: [
          { title: 'Zoeken op de website', content: 'Je website heeft een ingebouwde zoekfunctie. Bezoekers kunnen zoeken via het vergrootglas-icoon in de navigatie, of met Ctrl+K (Cmd+K op Mac).\n\nDe zoekfunctie doorzoekt automatisch alle paginas, posts en projecten.', order: 0 },
          { title: 'Afbeeldingen optimaliseren', content: 'Alle afbeeldingen worden automatisch geoptimaliseerd:\n\nGeconverteerd naar WebP-formaat (kleiner bestandsformaat)\nVerkleind naar meerdere varianten (1400px, 600px, 200px)\nKwaliteit op 85% (goede balans tussen kwaliteit en grootte)\n\nUpload altijd de hoogste kwaliteit. Het CMS maakt automatisch de juiste varianten aan.', order: 1 },
          { title: 'Veelgestelde vragen', content: 'Hoe maak ik een homepage?\nGa naar Menu, klik op het tandwiel-icoon naast een pagina en kies "Als homepage instellen".\n\nHoe voeg ik een contactformulier toe?\nOpen de page editor, klik op Componenten in de linker sidebar en klik op Formulier.\n\nHoe wijzig ik de footer?\nGa naar Instellingen en scroll naar het Footer gedeelte.\n\nMijn wijzigingen zijn niet zichtbaar?\nOp de productie-URL worden paginas 60 seconden gecached. Op de Vercel preview-URL zie je wijzigingen direct.', order: 2 }
        ]
      }
    ];

    for (const cat of defaults) {
      const catRef = doc(this.firestore, 'knowledge_base', cat.id);
      await setDoc(catRef, { title: cat.title, description: cat.description, order: cat.order });
      for (const art of cat.articles) {
        const artRef = doc(collection(this.firestore, 'knowledge_base', cat.id, 'articles'));
        await setDoc(artRef, { title: art.title, slug: this.toSlug(art.title), content: art.content, order: art.order });
      }
    }

    await this.loadFromFirestore();
  }

  get filteredCategories(): Category[] {
    if (!this.searchTerm.trim()) return this.categories;
    const term = this.searchTerm.toLowerCase();
    return this.categories
      .map(cat => ({
        ...cat,
        expanded: true,
        articles: cat.articles.filter(
          a => a.title.toLowerCase().includes(term) || a.content.toLowerCase().includes(term)
        )
      }))
      .filter(cat => cat.articles.length > 0);
  }

  toggleCategory(category: Category) {
    category.expanded = !category.expanded;
  }

  openArticle(category: Category, article: Article) {
    this.selectedArticle = { category, article };
    this.editingArticle = false;
    // Update URL without navigation
    this.router.navigate(['/knowledge-base', category.id, article.slug], { replaceUrl: true });
  }

  closeArticle() {
    this.selectedArticle = null;
    this.editingArticle = false;
    this.router.navigate(['/knowledge-base'], { replaceUrl: true });
  }

  private toSlug(text: string): string {
    return text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
  }

  get totalArticles(): number {
    return this.categories.reduce((sum, cat) => sum + cat.articles.length, 0);
  }

  // Edit article
  startEditArticle() {
    if (!this.selectedArticle) return;
    this.editTitle = this.selectedArticle.article.title;
    this.editBlocks = this.selectedArticle.article.blocks.map(b => ({ ...b }));
    if (this.editBlocks.length === 0) {
      this.editBlocks.push({ type: 'text', value: '' });
    }
    this.editingArticle = true;
  }

  async saveArticle() {
    if (!this.selectedArticle?.article.id) return;
    const catId = this.selectedArticle.category.id;
    const artId = this.selectedArticle.article.id;
    const artRef = doc(this.firestore, 'knowledge_base', catId, 'articles', artId);
    // Build plain text content from blocks for search
    const plainContent = this.editBlocks
      .filter(b => b.type !== 'image')
      .map(b => b.value)
      .join('\n\n');
    const newSlug = this.toSlug(this.editTitle);
    await setDoc(artRef, {
      title: this.editTitle,
      slug: newSlug,
      content: plainContent,
      blocks: this.editBlocks.map(b => ({ type: b.type, value: b.value })),
      order: this.selectedArticle.article.order
    }, { merge: true });
    this.selectedArticle.article.title = this.editTitle;
    this.selectedArticle.article.slug = newSlug;
    this.selectedArticle.article.content = plainContent;
    this.selectedArticle.article.blocks = this.editBlocks.map(b => ({ ...b }));
    this.editingArticle = false;
    this.router.navigate(['/knowledge-base', catId, newSlug], { replaceUrl: true });
  }

  cancelEditArticle() {
    this.editingArticle = false;
  }

  // Block management
  addBlock(type: 'text' | 'title' | 'image') {
    this.editBlocks.push({ type, value: '' });
  }

  removeBlock(index: number) {
    this.editBlocks.splice(index, 1);
  }

  moveBlockUp(index: number) {
    if (index <= 0) return;
    const temp = this.editBlocks[index];
    this.editBlocks[index] = this.editBlocks[index - 1];
    this.editBlocks[index - 1] = temp;
  }

  moveBlockDown(index: number) {
    if (index >= this.editBlocks.length - 1) return;
    const temp = this.editBlocks[index];
    this.editBlocks[index] = this.editBlocks[index + 1];
    this.editBlocks[index + 1] = temp;
  }

  async uploadBlockImage(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.uploadingBlockIndex = index;
    try {
      const variants = await this.imageUploadService.uploadImageWithVariants(file);
      this.editBlocks[index].value = variants.webp1400;
    } catch (e) {
      console.error('Upload failed:', e);
    } finally {
      this.uploadingBlockIndex = null;
      input.value = '';
    }
  }

  // Add category
  async addCategory() {
    if (!this.newCategoryTitle.trim()) return;
    const id = this.newCategoryTitle.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    const order = this.categories.length;
    const catRef = doc(this.firestore, 'knowledge_base', id);
    await setDoc(catRef, { title: this.newCategoryTitle, description: this.newCategoryDescription, order });
    this.categories.push({ id, title: this.newCategoryTitle, description: this.newCategoryDescription, articles: [], expanded: true, order });
    this.newCategoryTitle = '';
    this.newCategoryDescription = '';
    this.showAddCategory = false;
  }

  // Add article
  async addArticle(categoryId: string) {
    if (!this.newArticleTitle.trim()) return;
    const cat = this.categories.find(c => c.id === categoryId);
    if (!cat) return;
    const order = cat.articles.length;
    const artRef = doc(collection(this.firestore, 'knowledge_base', categoryId, 'articles'));
    const slug = this.toSlug(this.newArticleTitle);
    await setDoc(artRef, { title: this.newArticleTitle, slug, content: '', blocks: [], order });
    cat.articles.push({ id: artRef.id, slug, title: this.newArticleTitle, content: '', blocks: [], order });
    this.newArticleTitle = '';
    this.addingArticleToCategoryId = null;
  }

  // Delete article
  async deleteArticle(category: Category, article: Article) {
    if (!article.id || !confirm(`"${article.title}" verwijderen?`)) return;
    await deleteDoc(doc(this.firestore, 'knowledge_base', category.id, 'articles', article.id));
    category.articles = category.articles.filter(a => a.id !== article.id);
    if (this.selectedArticle?.article === article) {
      this.selectedArticle = null;
    }
  }

  // Force seed (admin only)
  async forceSeed() {
    if (!this.isAdmin || !confirm('Alle kennisbank content herladen? Bestaande content wordt overschreven.')) return;
    this.loading = true;
    // Delete existing
    for (const cat of this.categories) {
      for (const art of cat.articles) {
        if (art.id) await deleteDoc(doc(this.firestore, 'knowledge_base', cat.id, 'articles', art.id));
      }
      await deleteDoc(doc(this.firestore, 'knowledge_base', cat.id));
    }
    this.categories = [];
    await this.seedDefaultContent();
  }

  // Delete category
  async deleteCategory(category: Category) {
    if (!confirm(`Categorie "${category.title}" en alle artikelen verwijderen?`)) return;
    // Delete all articles first
    for (const art of category.articles) {
      if (art.id) {
        await deleteDoc(doc(this.firestore, 'knowledge_base', category.id, 'articles', art.id));
      }
    }
    await deleteDoc(doc(this.firestore, 'knowledge_base', category.id));
    this.categories = this.categories.filter(c => c.id !== category.id);
    if (this.selectedArticle?.category.id === category.id) {
      this.selectedArticle = null;
    }
  }
}
